import React from 'react';
import { Row, Col } from 'antd';
import './style.scss';
import { FormattedMessage } from 'react-intl';

export interface ITableOfContentItem {
  id: string;
  title: string;
}

// Recursive interface allowing nested subsections
export interface ISubSection {
  subTitle: string;
  image?: string;
  paragraphs: (string | JSX.Element)[];
  subSections?: ISubSection[];
}

export interface IContentSection {
  id: string;
  title: string;
  // If subSections is defined, allows multiple subtitles (blocks) within a section.
  subTitle?: string;
  image?: string;
  paragraphs?: (string | JSX.Element)[];
  subSections?: ISubSection[];
}

export interface IPageTemplateProps {
  headerImage: string;
  headerTitle: string;
  headerSubtitle?: string;
  tableOfContents: ITableOfContentItem[];
  contentSections: IContentSection[];
  className?: string;
  positionHeaderImg?: string;
}

// Recursive function to render table of contents entries and nested subsections
const renderTocItems = (
  subs: ISubSection[] | undefined,
  sectionId: string,
  prefix: string,
  depth = 1,
  scrollToSection: (id: string) => void
): React.ReactNode => {
  if (!subs) return null;
  return subs.map((sub, idx) => {
    const currentNumber = `${prefix}.${idx + 1}`;
    const anchorId = `${sectionId}-${currentNumber}`;
    return (
      <React.Fragment key={`${anchorId}`}>        
        <li className={`toc-item toc-subitem depth-${depth}`}>          
          <a
            href={`#${anchorId}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(anchorId);
            }}
            className="toc-link-subtitle"
          >
            {`${currentNumber} ${sub.subTitle}`}
          </a>
        </li>
        {sub.subSections &&
          renderTocItems(sub.subSections, sectionId, currentNumber, depth + 1, scrollToSection)
        }
      </React.Fragment>
    );
  });
};

// Recursive helper to render nested content subsections\ n
const renderSubSections = (
  sectionId: string,
  prefix: string,
  subs: ISubSection[]
): React.ReactNode => {
  return subs.map((sub, idx) => {
    const currentNumber = `${prefix}.${idx + 1}`;
    const sectionAnchorId = `${sectionId}-${currentNumber}`;
    return (
      <div key={sectionAnchorId} id={sectionAnchorId} className="sub-section">
        <h3 className="sub-section-title">
          {`${currentNumber} ${sub.subTitle}`}
        </h3>
        {sub.image && (
          <img
            src={sub.image}
            alt={sub.subTitle}
            className="sub-section-image"
          />
        )}
        {sub.paragraphs.map((para, pidx) => (
          <p key={`${sectionAnchorId}-p-${pidx}`}>{para}</p>
        ))}
        {sub.subSections && renderSubSections(sectionId, currentNumber, sub.subSections)}
      </div>
    );
  });
};

const PageTemplate: React.FC<IPageTemplateProps> = ({
  headerImage,
  headerTitle,
  headerSubtitle,
  tableOfContents,
  contentSections,
  className = '',
  positionHeaderImg = 'center'
}) => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`page-template-container ${className}`}>      
      <div
        className="page-template-header"
        style={{ backgroundImage: `url(${headerImage})`, backgroundPosition: positionHeaderImg }}
      >
        <div className="header-content">
          <h1 className="header-title">{headerTitle}</h1>
          {headerSubtitle && <p className="header-subtitle">{headerSubtitle}</p>}
        </div>
      </div>

      <div className="page-template-body">
        <Row gutter={24}>
          <Col xs={24} sm={24} md={6} lg={6} xl={6}>
            <div className="toc-container">
              <h3 className="toc-title"><FormattedMessage id="table.of.contents" /></h3>
              <ul className="toc-list">
                {tableOfContents.map((item) => {
                  const section = contentSections.find((sec) => sec.id === item.id);
                  const mainNumber = item.title.split('.')[0];
                  return (
                    <React.Fragment key={item.id}>
                      <li className="toc-item">
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            scrollToSection(item.id);
                          }}
                          className="toc-link"
                        >
                          {item.title}
                        </a>
                      </li>
                      {section?.subSections &&
                        renderTocItems(section.subSections, item.id, mainNumber, 1, scrollToSection)
                      }
                    </React.Fragment>
                  );
                })}
              </ul>
            </div>
          </Col>

          <Col xs={24} sm={24} md={18} lg={18} xl={18}>
            <div className="content-container">
              {contentSections.map((section) => (
                <div key={section.id} id={section.id} className="content-section">
                  <h2>{section.title}</h2>
                  {section.subSections
                    ? renderSubSections(section.id, section.title.split('.')[0], section.subSections)
                    : (
                      <>
                        {section.subTitle && <h3 className="section-subtitle">{section.subTitle}</h3>}
                        {section.image && (
                          <img
                            src={section.image}
                            alt={section.title}
                            className="section-image"
                          />
                        )}
                        {section.paragraphs &&
                          section.paragraphs.map((para, idx) => (
                            <p key={`para-${idx}`}>{para}</p>
                          ))}
                      </>
                    )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PageTemplate;
